// ViewAsSelector Component
// Allows profile owner to preview how their profile appears to different viewers

import React, { useState, useRef, useEffect } from 'react';
import { FaEye, FaGlobe, FaUsers, FaUser, FaChevronDown } from 'react-icons/fa';
import styles from './ViewAsSelector.module.css';

const VIEWER_TYPES = [
  {
    id: 'owner',
    label: 'Owner (You)',
    icon: <FaUser />,
    description: 'See your full profile as you see it',
    color: 'accent'
  },
  {
    id: 'public',
    label: 'Public Visitor',
    icon: <FaGlobe />,
    description: 'Non-authenticated users and search engines',
    color: 'green'
  },
  {
    id: 'club',
    label: 'Club Member',
    icon: <FaUsers />,
    description: 'Members of clubs you\'ve been part of',
    color: 'blue'
  }
];

const ViewAsSelector = ({ currentMode = 'owner', onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentViewer = VIEWER_TYPES.find(v => v.id === currentMode) || VIEWER_TYPES[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (viewerId) => {
    onChange(viewerId);
    setIsOpen(false);
  };

  return (
    <div className={styles.viewAsSelector} ref={dropdownRef}>
      <button
        className={`${styles.trigger} ${isOpen ? styles.active : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Select viewer type"
        aria-expanded={isOpen}
      >
        <FaEye className={styles.eyeIcon} />
        <span className={styles.label}>Viewing as:</span>
        <span className={`${styles.viewerName} ${styles[currentViewer.color]}`}>
          {currentViewer.icon}
          {currentViewer.label}
        </span>
        <FaChevronDown className={`${styles.chevron} ${isOpen ? styles.rotated : ''}`} />
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownHeader}>
            <FaEye />
            <span>Preview your profile as...</span>
          </div>
          
          {VIEWER_TYPES.map((viewer, index) => (
            <button
              key={viewer.id}
              type="button"
              className={`${styles.option} ${currentMode === viewer.id ? styles.selected : ''}`}
              onClick={() => handleSelect(viewer.id)}
              style={{ display: 'flex', opacity: 1, visibility: 'visible' }}
            >
              <div className={styles.optionContent} style={{ display: 'flex', opacity: 1 }}>
                <span className={`${styles.optionIcon} ${styles[viewer.color]}`} style={{ display: 'flex' }}>
                  {viewer.icon}
                </span>
                <div className={styles.optionText} style={{ display: 'flex', flexDirection: 'column' }}>
                  <span className={styles.optionLabel} style={{ color: 'white' }}>{viewer.label}</span>
                  <span className={styles.optionDescription} style={{ color: 'rgba(255, 255, 255, 0.6)' }}>{viewer.description}</span>
                </div>
              </div>
              {currentMode === viewer.id && (
                <div className={styles.checkmark} style={{ color: '#BEF264' }}>✓</div>
              )}
            </button>
          ))}

          <div className={styles.dropdownFooter}>
            <small>
              💡 Use this to see what different visitors can view on your profile
            </small>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewAsSelector;

